import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { CheckCircle2, Clock, AlertCircle, Mail, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public page required by Meta App Review.
 * - Explains how to delete data
 * - If ?code=… present, polls /api/meta/data-deletion/status and shows progress
 */
export default function DataDeletionPage() {
  const { language } = useLanguage();
  const es = language === 'es';
  const [params] = useSearchParams();
  const code = params.get('code') || '';
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!code) return;
    axios
      .get(`${API}/meta/data-deletion/status?code=${encodeURIComponent(code)}`)
      .then((r) => { setStatus(r.data); })
      .catch(() => { setStatus({ error: true }); });
  }, [code]);
  const loading = code && status === null;

  const title = es ? 'Eliminación de datos' : 'Data Deletion';
  const lead = es
    ? 'Cómo solicitar que eliminemos los datos asociados a tu cuenta WakaCasa.'
    : 'How to request that we delete data associated with your WakaCasa account.';

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="data-deletion-page">
      <div className="container mx-auto max-w-3xl py-12 px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 sm:p-12">
          <span className="inline-block text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">WakaCasa</span>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#0B192C] mb-3" data-testid="data-deletion-title">{title}</h1>
          <p className="text-slate-600 leading-relaxed mb-8">{lead}</p>

          {code && (
            <div className="border border-slate-200 rounded-xl p-5 mb-8 bg-slate-50" data-testid="data-deletion-status">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">
                {es ? 'Código de confirmación' : 'Confirmation code'}
              </div>
              <div className="font-mono text-sm text-[#0B192C] mb-3 break-all">{code}</div>
              {loading && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Clock className="w-4 h-4 animate-spin" /> {es ? 'Consultando estado…' : 'Checking status…'}
                </div>
              )}
              {status && status.error && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" /> {es ? 'Código no encontrado' : 'Code not found'}
                </div>
              )}
              {status && !status.error && (
                <div className="flex items-center gap-2 text-sm">
                  {status.status === 'completed' ? (
                    <><CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span className="text-emerald-700">{es ? 'Eliminación completada' : 'Deletion completed'}</span>
                      <span className="text-xs text-slate-400 ml-2">{status.completed_at}</span></>
                  ) : (
                    <><Clock className="w-4 h-4 text-amber-600" />
                      <span className="text-amber-700">{es ? 'Recibido, en proceso (máx. 30 días)' : 'Received, in progress (max 30 days)'}</span></>
                  )}
                </div>
              )}
            </div>
          )}

          <section className="mb-8">
            <h2 className="font-heading text-lg text-[#0B192C] mb-3">
              {es ? 'Opción 1 · Auto-eliminación desde tu cuenta' : 'Option 1 · Self-delete from your account'}
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              {es
                ? 'La forma más rápida — borra tu cuenta + todas tus propiedades, mensajes y datos asociados en menos de un minuto.'
                : 'The fastest way — wipes your account + all properties, messages and associated data in under a minute.'}
            </p>
            <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-600">
              <li>{es ? 'Inicia sesión en wakacasa.com' : 'Sign in at wakacasa.com'}</li>
              <li>{es ? 'Ve a tu Perfil (icono superior derecho)' : 'Go to your Profile (top-right icon)'}</li>
              <li>
                {es
                  ? 'Desplázate al final y haz clic en "Eliminar mi cuenta"'
                  : 'Scroll to the bottom and click "Delete my account"'}
              </li>
              <li>{es ? 'Confirma — la acción es inmediata e irreversible' : 'Confirm — the action is immediate and irreversible'}</li>
            </ol>
            <Link
              to="/profile"
              className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-[#0B192C] hover:text-[#D4AF37]"
              data-testid="data-deletion-profile-link"
            >
              <Trash2 className="w-4 h-4" />
              {es ? 'Ir a mi perfil →' : 'Open my profile →'}
            </Link>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-lg text-[#0B192C] mb-3">
              {es ? 'Opción 2 · Solicitud por correo' : 'Option 2 · Email request'}
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              {es
                ? 'Si no puedes iniciar sesión o prefieres una solicitud manual, escríbenos. Verificaremos tu identidad y eliminaremos tus datos dentro de 30 días.'
                : 'If you can\'t sign in or prefer a manual request, email us. We will verify your identity and delete your data within 30 days.'}
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-amber-900 mb-1">privacy@wakacasa.com</div>
                  <div className="text-xs text-amber-800">
                    {es
                      ? 'Asunto: "Solicitud de eliminación de datos". Incluye el correo registrado en WakaCasa.'
                      : 'Subject: "Data deletion request". Include the email registered on WakaCasa.'}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-2">
            <h2 className="font-heading text-lg text-[#0B192C] mb-3">
              {es ? '¿Qué se elimina exactamente?' : 'What exactly gets deleted?'}
            </h2>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-600">
              <li>{es ? 'Tu cuenta (correo, nombre, teléfono, foto, biografía, preferencias)' : 'Your account (email, name, phone, photo, bio, preferences)'}</li>
              <li>{es ? 'Tus propiedades publicadas y sus fotos' : 'Your published listings and their photos'}</li>
              <li>{es ? 'Mensajes que enviaste y revelaciones de contacto' : 'Messages you sent and contact reveals'}</li>
              <li>{es ? 'Documentos de verificación y favoritos' : 'Verification documents and favorites'}</li>
              <li>{es ? 'Métricas de actividad asociadas' : 'Associated activity metrics'}</li>
              <li>{es ? 'Correos programados (serie de bienvenida pendiente, resumen semanal)' : 'Scheduled emails (pending welcome series, weekly digest)'}</li>
            </ul>
            <p className="text-xs text-slate-400 mt-4">
              {es
                ? 'Lo que conservamos por obligación legal: comprobantes de pago anonimizados (7 años, sin tu correo ni notas). Logs de seguridad: 90 días.'
                : 'What we retain by legal obligation: anonymised payment proofs (7 years, no email or notes). Security logs: 90 days.'}
            </p>
          </section>

          <div className="mt-10 pt-6 border-t border-slate-100 flex flex-wrap gap-4 text-xs text-slate-400">
            <Link to="/privacy" className="hover:text-[#D4AF37]">{es ? 'Política de privacidad' : 'Privacy policy'}</Link>
            <Link to="/terms" className="hover:text-[#D4AF37]">{es ? 'Términos de uso' : 'Terms of service'}</Link>
            <span className="ml-auto">© {new Date().getFullYear()} WakaCasa CA</span>
          </div>
        </div>
      </div>
    </div>
  );
}
